import { useEffect, useState } from 'react';
import type { Refill } from '../../../stores/types';
import { formatDate, formatNumber } from '../../../utils/formatters';

export interface RefillDetailModalProps {
  isOpen: boolean;
  refills: Refill[];
  selectedRx: string | null;
  onClose: () => void;
}

function renderCopay(refill: Refill): string {
  if (refill.unableToCalculate) return 'Unable to calculate';
  if (refill.copayAmt < 0) return 'Copay unavailable';
  return `$${refill.copayAmt.toFixed(2)}`;
}

function renderStatus(raw: Record<string, unknown>): string {
  const status = String(raw.RefillStatus || '');
  return status.indexOf('After') === -1 ? status : `Refill ${status}`;
}

export function RefillDetailModal({
  isOpen,
  refills,
  selectedRx,
  onClose,
}: RefillDetailModalProps) {
  const initialIndex = refills.findIndex(r => r.rx === selectedRx);
  const [index, setIndex] = useState(initialIndex >= 0 ? initialIndex : 0);

  // Sync index when selectedRx changes (e.g., opening modal for different drug)
  useEffect(() => {
    const idx = refills.findIndex(r => r.rx === selectedRx);
    setIndex(idx >= 0 ? idx : 0);
  }, [selectedRx, refills]);

  // Escape key dismissal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || refills.length === 0) return null;

  const refill = refills[index] ?? refills[0];
  const raw = refill.raw;

  const status = renderStatus(raw);
  const doctor = [raw.DoctorFirstName, raw.DoctorLastName].filter(Boolean).join(' ');
  const instructions = String(raw.Instructions || '');
  const dose = formatNumber(Number(raw.UnitsPerDose || 0));
  const lastDispenseQty = formatNumber(Number(raw.LastDispenseQty || 0));
  const lastFill = formatDate(String(raw.LastDispenseDate || ''));
  const nextFill = formatDate(String(raw.NextRefillDate || ''));
  const expireDate = formatDate(String(raw.ExpirationDate || ''));
  const writtenQty = Number(raw.WrittenQty || 0);
  const unitsRemaining = formatNumber(refill.refillsLeft * writtenQty);
  const copayDisplay = renderCopay(refill);

  const canGoPrev = index > 0;
  const canGoNext = index < refills.length - 1;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade in"
        style={{ zIndex: 1040 }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="modal fade in"
        style={{ display: 'block', zIndex: 1050 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="refill-detail-title"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <button type="button" className="close" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
              <h4 className="modal-title" id="refill-detail-title">
                Rx Information
              </h4>
            </div>

            {/* Body */}
            <div className="modal-body">
              {/* Drug name + Rx# */}
              <div
                className="row"
                style={{ marginBottom: 20, borderBottom: '1px solid gainsboro', paddingBottom: 10 }}
              >
                <div className="col-sm-8">
                  <h2 style={{ marginTop: 0 }}>{refill.name}</h2>
                  <h3>Rx# {refill.rx}</h3>
                </div>
                <div className="col-sm-4" style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    style={{ width: '100%' }}
                    className={`btn btn-lg ${refill.refillClass}`}
                  >
                    {status}
                  </button>
                </div>
              </div>

              {/* Three-column detail */}
              <div className="row">
                {/* Doctor + Instructions */}
                <div className="col-sm-4" style={{ marginTop: 10 }}>
                  {doctor && (
                    <div>
                      <strong>Doctor: </strong>
                      {doctor}
                    </div>
                  )}
                  <div style={{ borderBottom: '1px solid gainsboro', marginTop: 8 }}>
                    <strong>Instructions</strong>
                  </div>
                  <div>{instructions || '—'}</div>
                </div>

                {/* Rx info table */}
                <div
                  className="col-sm-4"
                  style={{
                    marginTop: 10,
                    borderLeft: '1px solid gainsboro',
                    borderRight: '1px solid gainsboro',
                  }}
                >
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid gainsboro' }}>
                        <th colSpan={2}>Information</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Units Remaining:</td>
                        <td>{unitsRemaining}</td>
                      </tr>
                      <tr>
                        <td>Refills Remaining:</td>
                        <td>{formatNumber(refill.refillsLeft)}</td>
                      </tr>
                      <tr>
                        <td>Dose:</td>
                        <td>{dose}</td>
                      </tr>
                      <tr>
                        <td>Quantity Last Filled:</td>
                        <td>{lastDispenseQty}</td>
                      </tr>
                      <tr>
                        <td>Last Refilled:</td>
                        <td>{lastFill}</td>
                      </tr>
                      <tr>
                        <td>Next Refill:</td>
                        <td>{nextFill}</td>
                      </tr>
                      <tr>
                        <td>Expire Date:</td>
                        <td>{expireDate}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Copay */}
                <div className="col-sm-4" style={{ marginTop: 10 }}>
                  <div style={{ borderBottom: '1px solid gainsboro' }}>
                    <strong>
                      Your Estimated Co-pay<sup>*</sup>
                    </strong>
                  </div>
                  <h1>{copayDisplay}</h1>
                  <div style={{ fontSize: 12 }}>
                    <sup>*</sup>Estimated Co-pay Based on Previous Dispense
                  </div>
                </div>
              </div>
            </div>

            {/* Footer — Prev/Next navigation */}
            <div className="modal-footer" style={{ textAlign: 'center' }}>
              <div className="row">
                <div className="col-sm-4" style={{ textAlign: 'left' }}>
                  <button
                    type="button"
                    className={`btn btn-lg ${canGoPrev ? 'info-btn-success' : ''}`}
                    disabled={!canGoPrev}
                    onClick={() => setIndex(i => i - 1)}
                  >
                    &#8249; Prev Drug
                  </button>
                </div>
                <div className="col-sm-4">
                  <button type="button" className="btn btn-info btn-lg" onClick={onClose}>
                    Return
                  </button>
                </div>
                <div className="col-sm-4" style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className={`btn btn-lg ${canGoNext ? 'info-btn-success' : ''}`}
                    disabled={!canGoNext}
                    onClick={() => setIndex(i => i + 1)}
                  >
                    Next Drug &#8250;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
